import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "semantic-ui-react";

import { Can, ReactTable } from "components";
import { useRegisterNavAction } from "hooks";
import { SEASONS } from "models";
import { setTyresFilters, useTyresFilters } from "modules/TyresDashboard/components/TyresDashboardFilters/hooks";
import { useTyresQuery } from "modules/TyresDashboard/components/TyresDashboardTable/hooks";
import "modules/TyresDashboard/components/TyresDashboardTable/TyresDashboardTable.scss";
import { enumToDropdownOptions } from "utils/common";

import type { AccessorColumnDef, OnChangeFn, PaginationState } from "@tanstack/react-table";
import type { Tyre } from "models";
import type { PAGE_SIZE } from "utils/common";

type TyresDashboardTableProps = {
  onOpenEditModal: (tyre: Tyre) => void;
};

export const TyresDashboardTable = ({ onOpenEditModal }: TyresDashboardTableProps) => {
  const { t } = useTranslation();

  const { page: currentPage, page_size: pageSize } = useTyresFilters();
  const { data: tyres, refetch: refetchTyres, isFetching: isTyresFetching } = useTyresQuery();
  useRegisterNavAction(() => refetchTyres());

  const nbPages = tyres?.nb_pages ?? 0;

  const tyreSeasonFilterOptions = enumToDropdownOptions({ enumObj: SEASONS, t });
  const getTyreSeasonName = (season: SEASONS) => tyreSeasonFilterOptions.find(({ value }) => value === season)?.text ?? "";

  const handlePaginationChange: OnChangeFn<PaginationState> = updater => {
    const newState = typeof updater === "function" ? updater({ pageIndex: currentPage - 1, pageSize }) : updater;
    setTyresFilters({ page: newState.pageIndex + 1 });
  };

  const handlePageSizeChange = (page_size: PAGE_SIZE) => setTyresFilters({ page_size, page: 1 });

  const columns = useMemo<AccessorColumnDef<Tyre, string | number | null>[]>(
    () => [
      {
        id: "manufacturer",
        size: 180,
        accessorKey: "manufacturer",
        header: () => <span className="no-wrap-text">{t("v8_manufacturer", "Manufacturer")}</span>,
        cell: ({ getValue }) => getValue()
      },
      {
        id: "description",
        size: 300,
        accessorKey: "description",
        header: () => <span className="no-wrap-text">{t("v8_description", "Description")}</span>,
        cell: ({ getValue }) => getValue()
      },
      {
        id: "ean",
        size: 140,
        accessorKey: "ean",
        header: () => <span className="no-wrap-text">EAN</span>,
        cell: ({ getValue }) => getValue()
      },
      {
        id: "carry_capacity",
        size: 100,
        accessorKey: "carry_capacity",
        header: () => <span className="no-wrap-text">{t("v8_carry_capacity", "Carry capacity")}</span>,
        cell: ({ getValue }) => getValue()
      },
      {
        id: "width",
        size: 50,
        accessorKey: "width",
        header: () => <span className="no-wrap-text">{t("v8_width", "Width")}</span>,
        cell: ({ getValue }) => getValue()
      },
      {
        id: "height",
        size: 50,
        accessorKey: "height",
        header: () => <span className="no-wrap-text">{t("v8_height", "Height")}</span>,
        cell: ({ getValue }) => getValue()
      },
      {
        id: "size",
        size: 50,
        accessorKey: "size",
        header: () => <span className="no-wrap-text">{t("v8_size", "Size")}</span>,
        cell: ({ getValue }) => getValue()
      },
      {
        id: "speed",
        size: 50,
        accessorKey: "speed",
        header: () => <span className="no-wrap-text">{t("v8_speed", "Speed")}</span>,
        cell: ({ getValue }) => getValue()
      },
      {
        id: "season",
        size: 120,
        accessorKey: "season",
        header: () => <span className="no-wrap-text">{t("v8_season", "Season")}</span>,
        cell: ({ row: { original } }) => getTyreSeasonName(original.season)
      },
      {
        id: "actions",
        size: 40,
        accessorFn: row => row.id,
        header: () => null,
        cell: ({ row: { original: tyre } }) => (
          <Can I="update" the="tyres">
            <div className="actions-container">
              <div className="action-edit-button-container" onClick={() => onOpenEditModal(tyre)}>
                <Icon className="icon pen to square edit-icon action-button-icon-center" />
              </div>
            </div>
          </Can>
        )
      }
    ],
    [t, onOpenEditModal]
  );

  return (
    <div className="TyresDashboardTable">
      <ReactTable
        isFetching={isTyresFetching}
        columns={columns}
        data={tyres?.items ?? []}
        pagination={nbPages > 1 ? { pageIndex: currentPage - 1, pageSize } : undefined}
        pageCount={nbPages}
        pageSize={pageSize}
        totalItems={tyres?.nb_items ?? 0}
        onPaginationChange={handlePaginationChange}
        onChangePageSize={handlePageSizeChange}
      />
    </div>
  );
};
